<script setup>
import ModuleNavBar from './ModuleNavBar.vue';
import IBGLogo from '@/components/IBGLogo.vue';
import DisplayModeToggle from '@/components/DisplayModeToggle.vue';
import IconButton from '@/components/shared/IconButton.vue';
import DimmerButton from '@/components/dimmer/DimmerButton.vue';
import CategorySelector from '@/components/shared/CategorySelector.vue';
import { get } from 'lodash';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useGeneralStore } from '@/stores/general';
import { useDepartmentStore } from '@/stores/department';
import { useGalleriesStore } from '@/stores/galleries';
import { useActivitiesStore } from '@/stores/activities';
import { useNewsStore } from '@/stores/news';
import { useMealsStore } from '@/stores/meals';
import { useIdleHandlerStore } from '@/stores/idleHandler';
import InstitutionFilter from '@/components/shared/InstitutionFilter.vue';
import DepartmentSelectorFooter from '@/components/shared/DepartmentSelectorFooter.vue';
import { useInstitutionStore } from '@/stores/institution';
import DepartmentSelectorList from '@/components/shared/DepartmentSelectorList.vue';

const generalStore = useGeneralStore();
const route = useRoute();
const router = useRouter();
const departmentStore = useDepartmentStore();
const galleriesStore = useGalleriesStore();
const idleHandlerStore = useIdleHandlerStore();
const institutionFilterOpen = ref(false);
const showDepartmentsList = ref(false);
const institutionStore = useInstitutionStore();

const INSTITUTION_FILTER_MAP = {
  '/activities': useActivitiesStore(),
  '/news': useNewsStore(),
  '/mealPlan': useMealsStore(),
  '/galleries': useGalleriesStore(),
};

const useGlobalDepartmentSwitcher = computed(() => {
  const settingEnabled = get(
    departmentStore.settings,
    'screenConfig.departmentSwitcher',
    false
  );
  const hasTempKey = sessionStorage.getItem('temp_login_key');

  return settingEnabled || hasTempKey;
});

// Can toggle view if both list and grid is available.
const canToggleListView = computed(() => {
  const contentViews = route.meta.contentViews;
  return (
    contentViews &&
    contentViews.some((view) => view.name === 'list') &&
    contentViews.some((view) => view.name === 'grid')
  );
});

// Sets the contentView to handle cardType & positioning and activeViewType to handle list or grid view.
async function handleContentAndViewChange(viewChangedTo) {
  // Get the full contentView object that has been switched to then set store accordingly.
  const contentView = route.meta.contentViews.find(
    (view) => view.name === viewChangedTo
  );

  if (contentView) {
    await generalStore.setActiveContentView(contentView);

    if (viewChangedTo === 'list' || viewChangedTo === 'grid') {
      await generalStore.setActiveViewType(viewChangedTo);
    }
  } else {
    console.error('Changed contentView to non supported value:', viewChangedTo);
  }
}

const onDepartmentSelected = (department) => {
  showDepartmentsList.value = false;
  departmentStore.switchDepartment(department);
};

// Close department selector when screen goes idle
watch(
  () => idleHandlerStore.isIdle,
  (isIdle) => {
    if (isIdle) {
      showDepartmentsList.value = false;
    }
  }
);
</script>

<template>
  <div
    v-if="
      galleriesStore.categories?.length &&
      generalStore.activeViewType === 'list' &&
      route.path === '/galleries'
    "
    class="left-0 right-0 px-8 categories rounded-t-3xl relative shadow-topSmall py-8 pl-8 pr-0 pb-16"
  >
    <CategorySelector />
  </div>
  <div
    class="w-full relative rounded-t-3xl border-[3px] border-neutral-400 bg-neutral-300 z-40"
  >
    <!-- Toolbar -->
    <div
      class="flex flex-row items-center justify-between w-full absolute left-0 right-0 transform -translate-y-1/2 px-8"
    >
      <!-- Left container -->
      <div class="flex flex-1 justify-start">
        <IconButton
          v-show="route.path !== '/'"
          :icon="['fas', 'arrow-left']"
          :title="$t('global.backButton')"
          @click="router.back()"
          capitalizeTitle
        />
      </div>

      <!-- Logo -->
      <IBGLogo class="flex-none" />

      <!-- Right container -->
      <div class="flex flex-1 justify-end">
        <DisplayModeToggle
          v-if="canToggleListView"
          @view-changed="handleContentAndViewChange"
        />

        <DimmerButton v-if="route.path === '/'" />

        <IconButton
          v-if="route.path === '/bookings'"
          class="pl-3"
          :icon="['far', 'fa-xmark']"
          @click="router.push('/bookingsList')"
        />

        <IconButton
          v-if="
            INSTITUTION_FILTER_MAP[route.path]?.uniqueInstitutions?.length > 1
          "
          class="pl-3"
          :icon="['far', 'fa-sliders']"
          @click="institutionFilterOpen = !institutionFilterOpen"
        />

        <InstitutionFilter
          v-if="institutionFilterOpen"
          @close="institutionFilterOpen = false"
        />
      </div>
    </div>

    <!-- Footer overlay/background -->
    <div
      class="flex flex-col justify-center pt-12"
      :style="{ height: useGlobalDepartmentSwitcher ? '208px' : '250px' }"
    >
      <ModuleNavBar />
    </div>

    <div>
      <DepartmentSelectorFooter
        v-if="useGlobalDepartmentSwitcher && departmentStore.name"
        :is-open="showDepartmentsList"
        @toggle="showDepartmentsList = !showDepartmentsList"
        @close="showDepartmentsList = false"
      />
    </div>

    <DepartmentSelectorList
      v-if="showDepartmentsList"
      :selected-department="departmentStore"
      :departments="institutionStore.departments"
      @department-selected="onDepartmentSelected"
    />
  </div>
</template>
